import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { expandCapture, isOpen } from '@nexus/core';
import { addTaskToPage } from '@nexus/workspace';
import { useStore } from '../../db/context';
import { useProjects } from '../../db/tasks';
import { byDueThenPriority, todayIso } from './format';
import { ProgressBar } from './ProjectsView';
import { TaskRow } from './TaskRow';

/**
 * Shown on project pages: progress and all tasks that belong to the project,
 * from any page (links, task pages, subpages).
 */
export function ProjectPanel({ pageId, readOnly }: { pageId: string; readOnly?: boolean }) {
  const { t } = useTranslation();
  const store = useStore();
  const data = useProjects();
  const [text, setText] = useState('');
  const [showDone, setShowDone] = useState(false);
  const summary = data?.projects.find((p) => p.page.id === pageId);
  if (!data || !summary) return null;
  const own = data.tasks.filter((x) => x.projectId === pageId);
  const list = own.filter((x) => showDone || isOpen(x.status)).sort(byDueThenPriority);

  const add = async () => {
    const task = text.trim() ? expandCapture(text.trim(), todayIso()) : '';
    if (!task) return;
    // Clear first: the next task may already be typed while this one saves.
    setText('');
    await addTaskToPage(store, pageId, task);
  };

  return (
    <section aria-label={t('projects.tasks')} className="mt-6 rounded-xl border border-border p-4">
      <div className="flex items-center gap-3">
        <h2 className="text-sm font-semibold">{t('projects.tasks')}</h2>
        <div className="flex-1">
          <ProgressBar
            value={summary.progress}
            label={t('projects.progressOf', { title: summary.page.title })}
          />
        </div>
        <span className="text-xs text-fg-muted">
          {t('projects.progress', {
            done: summary.done,
            total: summary.done + summary.open,
            pct: Math.round(summary.progress * 100),
          })}
        </span>
      </div>
      <div className="mt-2">
        {list.map((x) => (
          <TaskRow
            key={x.id}
            task={x}
            showSource={x.pageId !== pageId}
            compact
            readOnly={readOnly}
          />
        ))}
        {list.length === 0 && (
          <p className="px-2 py-1 text-sm text-fg-muted">{t('projects.noTasks')}</p>
        )}
      </div>
      <div className="mt-2 flex items-center gap-2 px-2">
        {!readOnly && (
          <>
            <Plus className="size-4 text-fg-muted" />
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void add()}
              placeholder={t('projects.addTask')}
              aria-label={t('projects.addTask')}
              className="min-w-0 flex-1 bg-transparent py-1 text-sm outline-none"
            />
          </>
        )}
        {own.some((x) => !isOpen(x.status)) && (
          <label className="ml-auto flex items-center gap-1.5 text-xs text-fg-muted">
            <input
              type="checkbox"
              checked={showDone}
              onChange={(e) => setShowDone(e.target.checked)}
            />
            {t('tasks.showDone')}
          </label>
        )}
      </div>
    </section>
  );
}
